// @flow

import { push, replace } from "connected-react-router";
import { OktaAuth } from "@okta/okta-auth-js";

import Config from "../constants/Config";
import OktaConfig from "../constants/OktaConfig";
import ActionTypes from "../constants/ActionTypes";
import convertEmptyKeyToNull from "../utils/convertEmptyKeyToNull";
import userApi from "../api/user";
import { getUserNonce } from "../api";
import cookie from "../utils/cookie";
import GTM from "../utils/gtm";
import { getCart } from "./cart";

import {
    type LoginFormType,
    type RegisterFormType,
    type ProfileType,
} from "../types/UserTypes";

// Authenticate user (post login, register, first load)
// -------------

export const authenticateUser =
    (): any =>
    (dispatch: any, getState: any): any => {
        // set session auth status
        return dispatch({
            type: ActionTypes.AUTHENTICATED,
        });
    };

// Logout
// -------------

export const logout =
    (makeCall: any = true): any =>
    (dispatch: any, getState: any): any => {
        const { loggedIn } = getState().user;

        const handlePostLogoutActions = () => {
            return new Promise((resolve: any, reject: any): any => {
                // get new logged out nonce
                return getUserNonce(true).then(() => {
                    // reset cart
                    dispatch(getCart()).then(() => {
                        // logout
                        dispatch({
                            type: ActionTypes.LOGOUT,
                        });
                        // redirect to home
                        dispatch(push("/"));
                        resolve();
                    });
                });
            });
        };

        if (makeCall && loggedIn) {
            // user inititiated logout, call logout api
            return userApi
                .userLogout()
                .then((response): any => {
                    return handlePostLogoutActions();
                })
                .catch((error): any => {});
        } else {
            // expired token on server triggered user logout
            // dispatch(
            //     actions.globalModals.openAlertModal({
            //         title: "You have been logged out.",
            //         body: <React.Fragment></React.Fragment>,
            //         confirmText: "OK",
            //         onConfirm: () => {
            //             dispatch(actions.globalModals.closeAlertModal());
            //         },
            //     })
            // );

            return handlePostLogoutActions();
        }
    };

// Get User Info
// -------------

const getProfileRequest: any = (): any => ({
    type: ActionTypes.GET_PROFILE_REQUEST,
});

const getProfileSuccess: any = (profile: ProfileType): any => ({
    type: ActionTypes.GET_PROFILE_SUCCESS,
    profile,
});

const getProfileFail: any = (): any => ({
    type: ActionTypes.GET_PROFILE_FAIL,
});

export const getProfile =
    (loginAttempt: any, forceFetch?: boolean): any =>
    (dispatch: any, getState: any): any => {
        return new Promise((resolve: any, reject: any): any => {
            dispatch(getProfileRequest());
            return getUserNonce(forceFetch).then(nonceData => {
                if (nonceData?.is_logged_in) {
                    const { profile } = getState().user;
                    if (forceFetch || !Object.hasOwn(profile, "email")) {
                        return userApi
                            .userGetProfile()
                            .then((profile): any => {
                                dispatch(getProfileSuccess(profile));

                                if (loginAttempt) {
                                    dispatch(authenticateUser());
                                } else {
                                }

                                resolve();
                            })
                            .catch((err: any): any => {
                                dispatch(getProfileFail());

                                if (!loginAttempt) {
                                    dispatch(logout(false));
                                }

                                resolve();
                            });
                    } else {
                        resolve();
                        return dispatch(getProfileSuccess(profile));
                    }
                } else {
                    dispatch(getProfileFail());
                    resolve();
                }
            });
        });
    };

// Get Opt Ins (email/marketing preferences)
//

export const getOptIns =
    (): any =>
    (dispatch: any, getState: any): any => {
        return new Promise((resolve: any, reject: any): any => {
            return userApi
                .userGetOptIns()
                .then((optIns): any => {
                    dispatch({
                        type: ActionTypes.GET_MY_OPTIN_SUCCESS,
                        optIns: optIns,
                    });

                    resolve();
                })
                .catch((err: any): any => {
                    resolve();
                });
        });
    };

// Update Profile
// -----------

const updateProfileRequest: any = (): any => ({
    type: ActionTypes.UPDATE_PROFILE_REQUEST,
});

const updateProfileSuccess: any = (profile: ProfileType): any => ({
    type: ActionTypes.UPDATE_PROFILE_SUCCESS,
    profile,
});

const updateProfileFail: any = (error: any): any => ({
    type: ActionTypes.UPDATE_PROFILE_FAIL,
    error,
});

export const updateProfile =
    (profile: any): any =>
    (dispatch: any, getState: any): any => {
        dispatch(updateProfileRequest());

        return userApi
            .updateUserProfile(convertEmptyKeyToNull(profile))
            .then((response): any => {
                dispatch(updateProfileSuccess(profile));
            })
            .catch((err: any): any => {
                dispatch(getProfileFail(err));
                throw err;
            });
    };

// Update Optins
// -----------

export const updateOptins =
    (optins: any): any =>
    (dispatch: any, getState: any): any => {
        return userApi
            .updateUserOptins(optins)
            .then((response): any => {})
            .catch((err: any): any => {
                throw err;
            });
    };

// My Memberships
// -----------

// const getMyMembershipsRequest: any = (): any => ({
//     type: ActionTypes.GET_MY_MEMBERSHIPS_REQUEST,
// });

// const getMyMembershipsSuccess: any = (profile: ProfileType): any => ({
//     type: ActionTypes.GET_MY_MEMBERSHIPS_SUCCESS,
//     profile,
// });

// const getMyMembershipsFail: any = (): any => ({
//     type: ActionTypes.GET_MY_MEMBERSHIPS_FAIL,
// });

// export const getMyMemberships =
//     (loginAttempt: any): any =>
//     (dispatch: any, getState: any): any => {
//         return new Promise((resolve: any, reject: any): any => {
//             dispatch(getProfileRequest());

//             return userApi.userNonce().then((nonce): any => {
//                 userApi
//                     .userGetProfile(nonce.nonce)
//                     .then((profile): any => {
//                         dispatch(getProfileSuccess(profile));

//                         if (loginAttempt) {
//                             dispatch(authenticateUser(nonce.nonce));
//                         } else {
//                         }

//                         resolve();
//                     })
//                     .catch((err: any): any => {
//                         dispatch(getProfileFail());

//                         if (loginAttempt) {
//                             dispatch(logout(false));
//                         }

//                         resolve();
//                     });
//             });
//         });
//     };

// Register
// --------

const registerRequest = (): any => ({
    type: ActionTypes.REGISTER_REQUEST,
});

const registerSuccess =
    (): any =>
    (dispatch: any, getState: any): any => {
        dispatch({
            type: ActionTypes.REGISTER_SUCCESS,
        });

        dispatch(loginRegisterSuccess());

        // log GTM sign up event
        GTM.push({
            event: "sign_up",
            user_type: "public",
        });
    };

const registerFailure = (error: any = ""): any => ({
    type: ActionTypes.REGISTER_FAILURE,
    error,
});

export const register =
    (profile: RegisterFormType): any =>
    (dispatch: any, getState: any): any => {
        dispatch(registerRequest());
        return userApi
            .userRegister(profile)
            .then((response): any => {
                return dispatch(registerSuccess());
            })
            .catch((err: any): any => {
                dispatch(registerFailure());
                throw err;
            });
    };

// Login
// -----

const loginRequest = (): any => ({
    type: ActionTypes.LOGIN_REQUEST,
});

const loginFailure = (): any => ({
    type: ActionTypes.LOGIN_FAILURE,
});

const loginSuccess =
    (isSSOLogin?: boolean): any =>
    (dispatch: any, getState: any): any => {
        // login successful, set user object
        dispatch({
            type: ActionTypes.LOGIN_SUCCESS,
        });

        if (isSSOLogin) {
            dispatch({
                type: ActionTypes.OKTA_REDIRECT_FAILURE,
            });
        }

        dispatch(loginRegisterSuccess());
    };

export const login =
    (profile: LoginFormType): any =>
    (dispatch: any, getState: any): any => {
        dispatch(loginRequest());
        return userApi
            .userLogin(profile)
            .then((response): any => {
                return dispatch(loginSuccess());
            })
            .catch((err: any): any => {
                dispatch(loginFailure());
                throw err;
            });
    };

// Login/Register success
//

export const loginRegisterSuccess =
    (): any =>
    (dispatch: any, getState: any): any => {
        const { loginCallback } = getState().user;
        const loginRedirect = cookie.get("auts_login_redirect");

        dispatch({
            type: ActionTypes.NEW_LOGIN,
        });

        // get user data and set auth status
        dispatch(getProfile(true, true))
            .then(() => {
                return dispatch(getCart());
            })
            .then(() => {
                // handle redirect/callback
                if (loginCallback) {
                    dispatch(loginCallback());
                } else if (loginRedirect) {
                    dispatch(replace(loginRedirect));
                } else {
                    dispatch(replace("/"));
                }

                // unset redirect cookie and redux callback
                cookie.set("auts_login_redirect", "", 31);
                dispatch(clearLoginCallback());
            });
    };

// Okta Login
// -----

export const oktaLogin =
    (): any =>
    (dispatch: any, getState: any): any => {
        const authClient = new OktaAuth({
            ...OktaConfig,
        });

        return authClient.token.getWithRedirect({
            responseType: "id_token",
        });
    };

export const handleOktaRedirect =
    (): any =>
    (dispatch: any, getState: any): any => {
        return new Promise((resolve: any, reject: any): any => {
            dispatch({
                type: ActionTypes.OKTA_REDIRECT_REQUEST,
            });

            const authClient = new OktaAuth({
                ...OktaConfig,
            });

            return authClient.token
                .parseFromUrl()
                .then(res => {
                    if (res.tokens.accessToken.accessToken) {
                        return dispatch(
                            ssoLogin(res.tokens.accessToken.accessToken)
                        );
                    } else {
                        dispatch({
                            type: ActionTypes.OKTA_REDIRECT_FAILURE,
                        });
                        reject({
                            message: "No Okta access token was returned.",
                        });
                    }
                })
                .catch(error => {
                    dispatch({
                        type: ActionTypes.OKTA_REDIRECT_FAILURE,
                    });

                    reject(error);
                    console.error(error);
                });
        });
    };

export const ssoLogin =
    (token: string): any =>
    (dispatch: any, getState: any): any => {
        const { loggedIn } = getState().user;
        dispatch(loginRequest());

        const ssoApiToUse = loggedIn
            ? userApi.userSsoSwitch
            : userApi.userSsoLogin;

        return ssoApiToUse(token)
            .then((response): any => {
                if (response.new_user) {
                    // log GTM new okta sign up event
                    GTM.push({
                        event: "sign_up",
                        user_type: response.type || "okta",
                    });
                }

                return dispatch(loginSuccess(true));
            })
            .catch((err: any): any => {
                dispatch(loginFailure());

                dispatch({
                    type: ActionTypes.OKTA_REDIRECT_FAILURE,
                });

                throw err;
            });
    };

// Login/Register redux callback
//

export const setLoginCallback = (loginCallback: any): any => ({
    type: ActionTypes.SET_LOGIN_CALLBACK,
    loginCallback,
});

export const clearLoginCallback = (): any => ({
    type: ActionTypes.CLEAR_LOGIN_CALLBACK,
});

// Login with redirect
//

export const loginWithRedirect =
    (redirectUrl: string): any =>
    (dispatch: any, getState: any): any => {
        // set login redirect path
        cookie.set("auts_login_redirect", redirectUrl, 31);

        // redirect to login form
        dispatch(push(Config.routes.login));
    };

// Get all purchases events
//

export const getAllPurchasedEvents =
    (): any =>
    (dispatch: any, getState: any): any => {
        return new Promise((resolve: any, reject: any): any => {
            return userApi
                .userGetPurchasedEvents()
                .then((response): any => {
                    dispatch({
                        type: ActionTypes.GET_PURCHASED_EVENTS_SUCCESS,
                        purchasedEvents: response,
                    });

                    resolve();
                })
                .catch((err: any): any => {
                    resolve();
                });
        });
    };

// Change password
// -----------

export const changePassword =
    (current_password: string, new_password: string): any =>
    (dispatch: any, getState: any): any => {
        return userApi
            .userChangePassword(current_password, new_password)
            .then((response): any => {})
            .catch((err: any): any => {
                throw err;
            });
    };
